import React, { useState, useEffect } from 'react';
import '../styles/products.css';
import { fetchSheet } from "../utils/googleSheets";
import EnquiryForm from './EnquiryForm';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaFilter,
  FaTimes,
  FaEye,
  FaHeart,
  FaArrowRight,
  FaChevronDown,
  FaSearch,
  FaFire,
  FaTag,
  FaPalette,
  FaBath,
  FaHome,
  FaBuilding,
  FaLayerGroup,
  FaBed,
  FaTree,
  FaCouch,
  FaUtensils,
  FaTint,
  FaWarehouse,
  FaThermometerHalf,
  FaExpand,
  FaWhatsapp,
  FaEnvelope,
  FaRulerCombined,
  FaCube,
  FaPaintBrush,
  FaMapMarkerAlt,
  FaStore,
  FaPhone,
  FaArrowLeft,
  FaCheckCircle,
  FaShieldAlt,
  FaTruck,
  FaCalendarAlt,
  FaUser,
  FaBars,
  FaFillDrip
} from 'react-icons/fa';
import { FaStairs } from 'react-icons/fa6';
import logo from "../images/logo.png";

const getDriveImageUrl = (url) => {
  if (!url) return null;
  if (!url.includes("drive.google.com")) return url;

  let fileId = "";
  const match1 = url.match(/\/file\/d\/([^/]+)/);
  const match2 = url.match(/[?&]id=([^&]+)/);

  if (match1) fileId = match1[1];
  else if (match2) fileId = match2[1];

  if (!fileId) return null;
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
};

// Color mapping for common color names
const colorMap = {
  'white': '#FFFFFF',
  'black': '#000000',
  'grey': '#808080',
  'gray': '#808080',
  'brown': '#A52A2A',
  'beige': '#F5F5DC',
  'cream': '#FFFDD0',
  'ivory': '#FFFFF0',
  'off-white': '#FAF9F6',
  'charcoal': '#36454F',
  'silver': '#C0C0C0',
  'gold': '#FFD700',
  'blue': '#0000FF',
  'navy': '#000080',
  'sky blue': '#87CEEB',
  'light blue': '#ADD8E6',
  'red': '#FF0000',
  'burgundy': '#800020',
  'maroon': '#800000',
  'pink': '#FFC0CB',
  'rose': '#FF007F',
  'green': '#008000',
  'emerald': '#50C878',
  'olive': '#808000',
  'sage': '#9CAF88',
  'yellow': '#FFFF00',
  'mustard': '#FFDB58',
  'orange': '#FFA500',
  'terracotta': '#E2725B',
  'peach': '#FFE5B4',
  'purple': '#800080',
  'lavender': '#E6E6FA',
  'violet': '#8F00FF',
  'wood': '#966F33',
  'teak': '#B38B6D',
  'walnut': '#773F1A',
  'oak': '#806517',
  'sand': '#F4A460',
  'taupe': '#483C32',
  'khaki': '#C3B091',
  'stone': '#928E85',
  'concrete': '#9B9B9B',
  'slate': '#708090',
  'marble': '#E6E6FA',
  'granite': '#696969',
  'quartz': '#51484F'
};

const getColorValue = (colorName) => {
  if (!colorName) return '#808080';
  const normalized = colorName.toLowerCase().trim();
  return colorMap[normalized] || '#808080';
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTileType, setSelectedTileType] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const [selectedMaterial, setSelectedMaterial] = useState('all');
  const [selectedStyle, setSelectedStyle] = useState('all');
  const [selectedColor, setSelectedColor] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Get URL parameters
  const location = useLocation();
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Parse URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');

    if (categoryParam) {
      // Decode the category name
      const decodedCategory = decodeURIComponent(categoryParam);
      setSelectedCategory(decodedCategory);

      // Clear the URL parameter after using it
      navigate('/products', { replace: true });
    }
  }, [location.search, navigate]);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.style.overflow = !mobileMenuOpen ? 'hidden' : 'auto';
  };

  // Close mobile menu when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setMobileMenuOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  // Close mobile menu when clicking on a link
  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Helper function to split comma-separated values and get unique items
  const extractUniqueValues = (products, field) => {
    const allValues = new Set();
    products.forEach(product => {
      if (product[field]) {
        // Split by comma and trim each value
        const values = product[field].split(',').map(v => v.trim());
        values.forEach(v => {
          if (v) allValues.add(v);
        });
      }
    });
    return Array.from(allValues).sort();
  };

  const [tileTypes, setTileTypes] = useState([]);
  const [applicationAreas, setApplicationAreas] = useState([]);
  const [tileSizes, setTileSizes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tileStyles, setTileStyles] = useState([]);
  const [colors, setColors] = useState([]);

  // Helper functions for icons
  const getTileTypeIcon = (type) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes('floor')) return <FaHome />;
    if (typeLower.includes('wall')) return <FaPalette />;
    if (typeLower.includes('appliance')) return <FaThermometerHalf />;
    if (typeLower.includes('countertop')) return <FaExpand />;
    if (typeLower.includes('elevation')) return <FaBuilding />;
    return <FaLayerGroup />;
  };

  const getApplicationIcon = (app) => {
    const appLower = app.toLowerCase();
    if (appLower.includes('bathroom')) return <FaBath />;
    if (appLower.includes('kitchen')) return <FaUtensils />;
    if (appLower.includes('living')) return <FaCouch />;
    if (appLower.includes('bedroom')) return <FaBed />;
    if (appLower.includes('outdoor')) return <FaTree />;
    if (appLower.includes('commercial')) return <FaWarehouse />;
    if (appLower.includes('staircase')) return <FaStairs />;
    if (appLower.includes('water')) return <FaTint />;
    return <FaHome />;
  };

  const getSizeIcon = (size) => {
    return <FaRulerCombined />;
  };

  const getMaterialIcon = (material) => {
    return <FaCube />;
  };

  const getStyleIcon = (style) => {
    return <FaPaintBrush />;
  };

  const getColorIcon = (color) => {
    return <FaFillDrip />;
  };

  const getBadgeColor = (badge) => {
    if (!badge) return '';
    const badgeLower = badge.toLowerCase();
    if (badgeLower.includes('bestseller')) return 'bestseller';
    if (badgeLower.includes('trending')) return 'trending';
    if (badgeLower.includes('premium')) return 'premium';
    if (badgeLower.includes('new')) return 'new';
    if (badgeLower.includes('featured')) return 'featured';
    if (badgeLower.includes('sale')) return 'sale';
    return '';
  };

  const getBadgeIcon = (badge) => {
    if (!badge) return null;
    const badgeLower = badge.toLowerCase();
    if (badgeLower.includes('bestseller') || badgeLower.includes('trending')) return <FaFire />;
    if (badgeLower.includes('new')) return <FaTag />;
    return null;
  };

  // Open Quick View Modal
  const openQuickView = (product) => {
    setQuickViewProduct(product);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close Quick View Modal
  const closeQuickView = () => {
    setIsModalOpen(false);
    setQuickViewProduct(null);
    document.body.style.overflow = 'auto';
  };

  // Handle Enquire Now with WhatsApp redirection - FIXED
  const handleEnquireNow = (e, product = null) => {
    // If it's called with a product (from WhatsApp button), redirect to WhatsApp
    if (product) {
      const productName = product.name || 'this product';
      const message = encodeURIComponent(`Hi, I'm interested in ${productName}. Could you please provide more details?`);
      const phoneNumber = '919942169120'; // Updated WhatsApp business number
      // Use window.location.href for better compatibility (no popup blocker issues)
      window.location.href = `https://wa.me/${phoneNumber}?text=${message}`;
      return; // Return early to prevent form submission
    }

    // If it's an event (from form submission), prevent default and show form
    if (e && e.preventDefault) {
      e.preventDefault();
      setShowEnquiryForm(true);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsData, categoriesData] = await Promise.all([
          fetchSheet("AllProducts"),
          fetchSheet("Categories")
        ]);

        // Process products data
        const normalizedProducts = productsData.map((p, index) => {
          // Clean and split comma-separated fields
          const cleanField = (field) => {
            if (!field) return '';
            return field.split(',').map(v => v.trim()).join(', ');
          };

          return {
            id: p.id || index + 1,
            name: p.name || 'Tile Product',
            category: p.category || 'Other',
            image: getDriveImageUrl(p.image) || 'https://via.placeholder.com/300x200?text=Tile+Image',
            badge: p.badge || '',
            type: cleanField(p.type) || '',
            size: cleanField(p.size) || '',
            material: cleanField(p.material) || '',
            style: cleanField(p.style) || '',
            application: cleanField(p.application) || '',
            color: cleanField(p.color) || '', // Add color field
            description: `High-quality ${p.material || 'ceramic'} tiles perfect for ${p.application || 'various spaces'}. ${p.style || 'Elegant'} finish with durable construction.`,
            features: [
              'Water Resistant',
              'Scratch Proof',
              'Easy to Clean',
              'Long Lasting',
              'Low Maintenance'
            ]
          };
        });

        // Process categories data
        const normalizedCategories = categoriesData.map((c, index) => ({
          id: c.id || index + 1,
          name: c.name || 'Category',
          image: getDriveImageUrl(c.image) || 'https://via.placeholder.com/300x200?text=Category',
          description: c.description || ''
        }));

        // Set states
        setProducts(normalizedProducts);
        setFilteredProducts(normalizedProducts);
        setCategories(normalizedCategories);

        // Extract dynamic values
        setTileTypes(extractUniqueValues(normalizedProducts, 'type'));
        setApplicationAreas(extractUniqueValues(normalizedProducts, 'application'));
        setTileSizes(extractUniqueValues(normalizedProducts, 'size'));
        setMaterials(extractUniqueValues(normalizedProducts, 'material'));
        setTileStyles(extractUniqueValues(normalizedProducts, 'style'));
        setColors(extractUniqueValues(normalizedProducts, 'color')); // Extract colors

        setLoading(false);

      } catch (err) {
        console.error("Error loading data:", err);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [
    selectedCategory,
    selectedTileType,
    selectedApplication,
    selectedSize,
    selectedMaterial,
    selectedStyle,
    selectedColor,
    sortBy,
    searchQuery,
    products
  ]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Reset to first page when filters change
    setCurrentPage(1);

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product =>
        product.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by tile type
    if (selectedTileType !== 'all') {
      filtered = filtered.filter(product => {
        if (!product.type) return false;
        const typeList = product.type.split(',').map(t => t.trim().toLowerCase());
        return typeList.includes(selectedTileType.toLowerCase());
      });
    }

    // Filter by application area
    if (selectedApplication !== 'all') {
      filtered = filtered.filter(product => {
        if (!product.application) return false;
        const appList = product.application.split(',').map(a => a.trim().toLowerCase());
        return appList.includes(selectedApplication.toLowerCase());
      });
    }

    // Filter by size
    if (selectedSize !== 'all') {
      filtered = filtered.filter(product => {
        if (!product.size) return false;
        const sizeList = product.size.split(',').map(s => s.trim().toLowerCase());
        return sizeList.includes(selectedSize.toLowerCase());
      });
    }

    // Filter by material
    if (selectedMaterial !== 'all') {
      filtered = filtered.filter(product => {
        if (!product.material) return false;
        const materialList = product.material.split(',').map(m => m.trim().toLowerCase());
        return materialList.includes(selectedMaterial.toLowerCase());
      });
    }

    // Filter by style
    if (selectedStyle !== 'all') {
      filtered = filtered.filter(product => {
        if (!product.style) return false;
        const styleList = product.style.split(',').map(s => s.trim().toLowerCase());
        return styleList.includes(selectedStyle.toLowerCase());
      });
    }

    // Filter by color
    if (selectedColor !== 'all') {
      filtered = filtered.filter(product => {
        if (!product.color) return false;
        const colorList = product.color.split(',').map(c => c.trim().toLowerCase());
        return colorList.includes(selectedColor.toLowerCase());
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        product.material?.toLowerCase().includes(query) ||
        product.style?.toLowerCase().includes(query) ||
        product.application?.toLowerCase().includes(query) ||
        product.type?.toLowerCase().includes(query) ||
        product.size?.toLowerCase().includes(query) ||
        product.color?.toLowerCase().includes(query)
      );
    }

    // Sort products
    switch (sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'category':
        filtered.sort((a, b) => a.category.localeCompare(b.category));
        break;
      default: // 'featured'
        filtered.sort((a, b) => {
          const aFeatured = a.badge && a.badge.toLowerCase().includes('featured');
          const bFeatured = b.badge && b.badge.toLowerCase().includes('featured');
          if (aFeatured && !bFeatured) return -1;
          if (!aFeatured && bFeatured) return 1;
          return a.name.localeCompare(b.name);
        });
    }

    setFilteredProducts(filtered);
  };

  const handleTileTypeClick = (type) => {
    setSelectedTileType(type === selectedTileType ? 'all' : type);
  };

  const handleApplicationClick = (app) => {
    setSelectedApplication(app === selectedApplication ? 'all' : app);
  };

  const handleSizeClick = (size) => {
    setSelectedSize(size === selectedSize ? 'all' : size);
  };

  const handleMaterialClick = (material) => {
    setSelectedMaterial(material === selectedMaterial ? 'all' : material);
  };

  const handleStyleClick = (style) => {
    setSelectedStyle(style === selectedStyle ? 'all' : style);
  };

  const handleColorClick = (color) => {
    setSelectedColor(color === selectedColor ? 'all' : color);
  };

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedTileType('all');
    setSelectedApplication('all');
    setSelectedSize('all');
    setSelectedMaterial('all');
    setSelectedStyle('all');
    setSelectedColor('all');
    setSearchQuery('');
    setSortBy('featured');
  };

  const getFilterCount = () => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (selectedTileType !== 'all') count++;
    if (selectedApplication !== 'all') count++;
    if (selectedSize !== 'all') count++;
    if (selectedMaterial !== 'all') count++;
    if (selectedStyle !== 'all') count++;
    if (selectedColor !== 'all') count++;
    return count;
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="products-page">
      <EnquiryForm
        isOpen={showEnquiryForm}
        onClose={() => setShowEnquiryForm(false)}
      />
      {/* Header */}
      <header className={`main-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container header-container">
          <div className="logo-container">
            <img src={logo} alt="Logo" style={{ width: 50, height: 50 }} />
            <div className="logo-text">
              <h1>Shree Krishna Tiles</h1>
            </div>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}>
            <ul>
              <li><a href="/" onClick={handleNavLinkClick}>Home</a></li>
              <li><a href="/#categories" onClick={handleNavLinkClick}>Categories</a></li>
              <li><a href="#" onClick={handleNavLinkClick}>Products</a></li>
              <li><a href="/#about" onClick={handleNavLinkClick}>About Us</a></li>
              <li><a href="/#testimonials" onClick={handleNavLinkClick}>Testimonials</a></li>
              <li><a href="/blog" onClick={handleNavLinkClick}>Blog</a></li>
              <li><a href="/contact" onClick={handleNavLinkClick}>Contact</a></li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Enquire Now Button - Fixed Position */}
      <button className="enquire-now-btn" onClick={(e) => handleEnquireNow(e)}>
        <span>Enquire Now</span>
        <FaArrowRight className="arrow-icon" />
      </button>

      <main className="products-main">
        <div className="container">
          {/* Page Header */}
          <div className="page-header">
            <h1>Our Tile Collection</h1>
            <p>Discover premium tiles for every space, style, and application</p>
          </div>

          {/* Quick Filter Tabs */}
          <div className="quick-filter-tabs">
            <div className="tabs-header">
              <h3>Browse by Application</h3>
              <p>Tap to filter tiles by room or area</p>
            </div>
            <div className="tabs-grid">
              <button
                className={`tab-btn ${selectedApplication === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedApplication('all')}
              >
                <span className="tab-icon"><FaLayerGroup /></span>
                <span className="tab-label">All Areas</span>
              </button>

              {applicationAreas.map((app) => (
                <button
                  key={app}
                  className={`tab-btn ${selectedApplication === app ? 'active' : ''}`}
                  onClick={() => handleApplicationClick(app)}
                >
                  <span className="tab-icon">{getApplicationIcon(app)}</span>
                  <span className="tab-label">{app}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="products-layout">
            {/* Filters Sidebar */}
            <aside className={`filters-sidebar ${mobileFilterOpen ? 'mobile-open' : ''}`}>
              <div className="filters-header">
                <div className="filter-title">
                  <FaFilter />
                  <h3>Filters {getFilterCount() > 0 && `(${getFilterCount()})`}</h3>
                </div>
                <div className="filter-actions">
                  <button
                    className="clear-filters"
                    onClick={clearAllFilters}
                    disabled={getFilterCount() === 0}
                  >
                    Clear All
                  </button>
                  <button
                    className="mobile-close-btn"
                    onClick={() => setMobileFilterOpen(false)}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="filter-section">
                <div className="filter-search">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Categories Filter */}
              <div className="filter-section">
                <h4>Categories</h4>
                <div className="categories-list">
                  <button
                    className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedCategory('all')}
                  >
                    <FaCube />
                    <span>All Categories</span>
                    <span className="count">{products.length}</span>
                  </button>

                  {categories.map(category => (
                    <button
                      key={category.id}
                      className={`category-btn ${selectedCategory === category.name ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category.name)}
                    >
                      <FaCube />
                      <span>{category.name}</span>
                      <span className="count">
                        {products.filter(p => p.category === category.name).length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tile Type Filter */}
              {tileTypes.length > 0 && (
                <div className="filter-section">
                  <h4>Tile Type</h4>
                  <div className="type-filters">
                    <button
                      className={`type-btn ${selectedTileType === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedTileType('all')}
                    >
                      <FaLayerGroup />
                      <span>All Types</span>
                    </button>

                    {tileTypes.map((type) => (
                      <button
                        key={type}
                        className={`type-btn ${selectedTileType === type ? 'active' : ''}`}
                        onClick={() => handleTileTypeClick(type)}
                      >
                        {getTileTypeIcon(type)}
                        <span>{type}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Filter - NEW SECTION */}
              {colors.length > 0 && (
                <div className="filter-section">
                  <h4>Color</h4>
                  <div className="material-filters">
                    <button
                      className={`material-btn ${selectedColor === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedColor('all')}
                    >
                      <FaFillDrip />
                      <span>All Colors</span>
                    </button>

                    {colors.map(color => (
                      <button
                        key={color}
                        className={`material-btn ${selectedColor === color ? 'active' : ''}`}
                        onClick={() => handleColorClick(color)}
                        title={color}
                      >
                        <span className="color-swatch" style={{ backgroundColor: getColorValue(color) }}></span>
                        <span className="material-label">{color}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}


              {/* Materials Filter */}
              {materials.length > 0 && (
                <div className="filter-section">
                  <h4>Material</h4>
                  <div className="material-filters">
                    <button
                      className={`material-btn ${selectedMaterial === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedMaterial('all')}
                    >
                      <FaCube /> All Materials
                    </button>

                    {materials.map(material => (
                      <button
                        key={material}
                        className={`material-btn ${selectedMaterial === material ? 'active' : ''}`}
                        onClick={() => handleMaterialClick(material)}
                      >
                        <FaCube /> {material}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Styles Filter */}
              {tileStyles.length > 0 && (
                <div className="filter-section">
                  <h4>Style & Finish</h4>
                  <div className="style-filters">
                    <button
                      className={`style-btn ${selectedStyle === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedStyle('all')}
                    >
                      <FaPaintBrush /> All Styles
                    </button>

                    {tileStyles.map(style => (
                      <button
                        key={style}
                        className={`style-btn ${selectedStyle === style ? 'active' : ''}`}
                        onClick={() => handleStyleClick(style)}
                      >
                        <FaPaintBrush /> {style}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sizes Filter */}
              {tileSizes.length > 0 && (
                <div className="filter-section">
                  <h4>Tile Sizes</h4>
                  <div className="size-filters">
                    <button
                      className={`size-btn ${selectedSize === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedSize('all')}
                    >
                      <FaRulerCombined /> All Sizes
                    </button>

                    {tileSizes.map(size => (
                      <button
                        key={size}
                        className={`size-btn ${selectedSize === size ? 'active' : ''}`}
                        onClick={() => handleSizeClick(size)}
                      >
                        <FaRulerCombined /> {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}



              {/* Active Filters */}
              {getFilterCount() > 0 && (
                <div className="active-filters">
                  <h4>Active Filters</h4>
                  <div className="filter-tags">
                    {selectedCategory !== 'all' && (
                      <span className="filter-tag">
                        Category: {selectedCategory}
                        <FaTimes onClick={() => setSelectedCategory('all')} />
                      </span>
                    )}
                    {selectedTileType !== 'all' && (
                      <span className="filter-tag">
                        Type: {selectedTileType}
                        <FaTimes onClick={() => setSelectedTileType('all')} />
                      </span>
                    )}
                    {selectedApplication !== 'all' && (
                      <span className="filter-tag">
                        Application: {selectedApplication}
                        <FaTimes onClick={() => setSelectedApplication('all')} />
                      </span>
                    )}
                    {selectedSize !== 'all' && (
                      <span className="filter-tag">
                        Size: {selectedSize}
                        <FaTimes onClick={() => setSelectedSize('all')} />
                      </span>
                    )}
                    {selectedMaterial !== 'all' && (
                      <span className="filter-tag">
                        Material: {selectedMaterial}
                        <FaTimes onClick={() => setSelectedMaterial('all')} />
                      </span>
                    )}
                    {selectedStyle !== 'all' && (
                      <span className="filter-tag">
                        Style: {selectedStyle}
                        <FaTimes onClick={() => setSelectedStyle('all')} />
                      </span>
                    )}
                    {selectedColor !== 'all' && (
                      <span className="filter-tag">
                        Color: {selectedColor}
                        <FaTimes onClick={() => setSelectedColor('all')} />
                      </span>
                    )}
                  </div>
                </div>
              )}
            </aside>

            {/* Main Content */}
            <div className="products-content">
              {/* Products Toolbar */}
              <div className="products-toolbar">
                <div className="toolbar-left">
                  <button
                    className="mobile-filter-btn"
                    onClick={() => setMobileFilterOpen(true)}
                  >
                    <FaFilter /> Filters {getFilterCount() > 0 && `(${getFilterCount()})`}
                  </button>
                  <div className="results-info">
                    <span className="results-count">{filteredProducts.length} Products</span>
                    <span className="results-total">of {products.length} total</span>
                  </div>
                </div>

                <div className="toolbar-right">
                  <div className="sort-dropdown">
                    <label>Sort by:</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="featured">Featured</option>
                      <option value="name-asc">Name: A to Z</option>
                      <option value="name-desc">Name: Z to A</option>
                      <option value="category">Category</option>
                    </select>
                  </div>

                  {/* Items per page selector */}
                  <div className="sort-dropdown">
                    <label htmlFor="itemsPerPage">Items per page: </label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="items-per-page-select"
                    >
                      <option value={6}>6</option>
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              {filteredProducts.length === 0 ? (
                <div className="no-products">
                  <div className="no-products-icon">
                    <FaSearch />
                  </div>
                  <h3>No tiles found</h3>
                  <p>Try adjusting your filters or search term to find what you're looking for</p>
                  <button
                    className="btn btn-primary"
                    onClick={clearAllFilters}
                  >
                    <FaTimes /> Clear All Filters
                  </button>
                </div>
              ) : (
                <>
                  <div className="products-grid">
                    {currentItems.map((product) => (
                      <div key={product.id} className="product-card">
                        <div className="product-header">
                          {product.badge && (
                            <div className={`product-badge ${getBadgeColor(product.badge)}`}>
                              {getBadgeIcon(product.badge)}
                              {product.badge}
                            </div>
                          )}
                        </div>

                        <div className="product-image">
                          <img src={product.image} alt={product.name} />
                          <div className="product-overlay">
                            <button
                              className="quick-view-btn"
                              onClick={() => openQuickView(product)}
                            >
                              <FaEye /> Quick View
                            </button>
                          </div>
                        </div>

                        <div className="product-info">
                          <div className="product-tags">
                            {product.type && product.type.split(',').map((type, index) => (
                              <span key={index} className="product-tag type-tag">
                                {getTileTypeIcon(type)}
                                {type.trim()}
                              </span>
                            ))}
                          </div>

                          <h3 className="product-title">{product.name}</h3>
                          <p className="product-category">{product.category}</p>

                          <div className="product-details">
                            {product.application && (
                              <div className="detail-item">
                                <strong>Application:</strong> {product.application}
                              </div>
                            )}
                            {product.size && (
                              <div className="detail-item">
                                <strong>Size:</strong> {product.size}
                              </div>
                            )}
                            {product.material && (
                              <div className="detail-item">
                                <strong>Material:</strong> {product.material}
                              </div>
                            )}
                            {product.style && (
                              <div className="detail-item">
                                <strong>Style:</strong> {product.style}
                              </div>
                            )}
                          </div>

                          <div className="product-actions">
                            <button
                              className="btn btn-primary enquire-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEnquireNow(null, product);
                              }}
                            >
                              <FaWhatsapp /> WhatsApp Enquiry
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {filteredProducts.length > 6 && (
                    <div className="pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`pagination-btn prev-btn ${currentPage === 1 ? 'disabled' : ''}`}
                        aria-label="Previous page"
                      >
                        <FaArrowRight style={{ transform: 'rotate(180deg)' }} />
                        <span>Previous</span>
                      </button>

                      <div className="page-numbers">
                        {/* Always show first page */}
                        <button
                          onClick={() => paginate(1)}
                          className={`page-btn ${currentPage === 1 ? 'active' : ''}`}
                        >
                          1
                        </button>

                        {/* Show ellipsis if needed after first page */}
                        {currentPage > 3 && (
                          <span className="page-ellipsis">...</span>
                        )}

                        {/* Show page before current if not first or second page */}
                        {currentPage > 2 && (
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            className="page-btn"
                          >
                            {currentPage - 1}
                          </button>
                        )}

                        {/* Show current page if not first or last */}
                        {currentPage > 1 && currentPage < Math.ceil(filteredProducts.length / itemsPerPage) && (
                          <button className="page-btn active">
                            {currentPage}
                          </button>
                        )}

                        {/* Show page after current if not last or second to last */}
                        {currentPage < Math.ceil(filteredProducts.length / itemsPerPage) - 1 && (
                          <button
                            onClick={() => paginate(currentPage + 1)}
                            className="page-btn"
                          >
                            {currentPage + 1}
                          </button>
                        )}

                        {/* Show ellipsis if needed before last page */}
                        {currentPage < Math.ceil(filteredProducts.length / itemsPerPage) - 2 && (
                          <span className="page-ellipsis">...</span>
                        )}

                        {/* Always show last page if there is more than one page */}
                        {Math.ceil(filteredProducts.length / itemsPerPage) > 1 && (
                          <button
                            onClick={() => paginate(Math.ceil(filteredProducts.length / itemsPerPage))}
                            className={`page-btn ${currentPage === Math.ceil(filteredProducts.length / itemsPerPage) ? 'active' : ''}`}
                          >
                            {Math.ceil(filteredProducts.length / itemsPerPage)}
                          </button>
                        )}
                      </div>

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredProducts.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(filteredProducts.length / itemsPerPage)}
                        className={`pagination-btn next-btn ${currentPage === Math.ceil(filteredProducts.length / itemsPerPage) ? 'disabled' : ''}`}
                        aria-label="Next page"
                      >
                        <span>Next</span>
                        <FaArrowRight />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Call to Action */}
      <section className="products-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Need Professional Advice?</h2>
            <p>Our tile experts can help you choose the perfect tiles for your project</p>
            <div className="cta-buttons">
              <a href="tel:+919942169120" className="btn btn-primary btn-large">
                <FaPhone /> Call Expert
              </a>
              <a href="https://wa.me/919942169120" target='_blank' className="btn btn-secondary btn-large">
                <FaWhatsapp /> WhatsApp Consultation
              </a>
              <a href="/#contact" className="btn btn-outline btn-large">
                <FaStore /> Visit Showroom
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick View Modal */}
      {isModalOpen && quickViewProduct && (
        <div className="modal-overlay" onClick={closeQuickView}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeQuickView}>
              <FaTimes />
            </button>

            <div className="modal-body">
              {/* Product Image */}
              <div className="modal-image-container">
                <img src={quickViewProduct.image} alt={quickViewProduct.name} />
                {quickViewProduct.badge && (
                  <div className={`modal-badge ${getBadgeColor(quickViewProduct.badge)}`}>
                    {getBadgeIcon(quickViewProduct.badge)}
                    {quickViewProduct.badge}
                  </div>
                )}

                <div className="modal-contact mobile-view">
                  <h3>Get Professional Consultation</h3>
                  <div className="contact-options">
                    <button
                      className="btn btn-primary modal-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEnquireNow(null, quickViewProduct);
                      }}
                    >
                      <FaWhatsapp /> WhatsApp Enquiry
                    </button>
                    <a
                      href="tel:+919942169120"
                      className="btn btn-secondary modal-btn"
                    >
                      <FaPhone /> Call Now
                    </a>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="modal-details">
                <div className="modal-header">
                  <h2>{quickViewProduct.name}</h2>
                  <p className="modal-category">{quickViewProduct.category}</p>
                </div>

                <div className="modal-description">
                  <p>{quickViewProduct.description}</p>
                </div>

                {/* Product Specifications */}
                <div className="modal-specs">
                  <h3>Product Specifications</h3>
                  <div className="specs-grid">
                    {quickViewProduct.type && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaLayerGroup /> Type
                        </span>
                        <span className="spec-value">{quickViewProduct.type}</span>
                      </div>
                    )}
                    {quickViewProduct.application && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaHome /> Application
                        </span>
                        <span className="spec-value">{quickViewProduct.application}</span>
                      </div>
                    )}
                    {quickViewProduct.size && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaRulerCombined /> Size
                        </span>
                        <span className="spec-value">{quickViewProduct.size}</span>
                      </div>
                    )}
                    {quickViewProduct.material && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaCube /> Material
                        </span>
                        <span className="spec-value">{quickViewProduct.material}</span>
                      </div>
                    )}
                    {quickViewProduct.style && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaPaintBrush /> Style
                        </span>
                        <span className="spec-value">{quickViewProduct.style}</span>
                      </div>
                    )}
                    {quickViewProduct.color && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaFillDrip /> Colors
                        </span>
                        <span className="spec-value">
                          {quickViewProduct.color.split(',').map((color, index) => (
                            <span key={index} className="color-chip">
                              <span
                                className="color-chip-dot"
                                style={{ backgroundColor: getColorValue(color) }}
                              ></span>
                              {color.trim()}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Options */}
                <div className="modal-contact pc-view">
                  <h3>Get Professional Consultation</h3>
                  <div className="contact-options">
                    <button
                      className="btn btn-primary modal-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEnquireNow(null, quickViewProduct);
                      }}
                    >
                      <FaWhatsapp /> WhatsApp Enquiry
                    </button>
                    <a
                      href="tel:+919942169120"
                      className="btn btn-secondary modal-btn"
                    >
                      <FaPhone /> Call Now
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filter Overlay */}
      {mobileFilterOpen && (
        <div
          className="mobile-filter-overlay"
          onClick={() => setMobileFilterOpen(false)}
        />
      )}
    </div>
  );
};

export default Products;